import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TheaterComedy as TheaterIcon,
  TrendingUp as RevenueIcon,
  Visibility as ViewsIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';

/**
 * MetricsVisualization Component
 * 
 * Extracts and visualizes budget recommendations, box office projections,
 * theater counts, and revenue data from analysis text.
 */

const MetricsVisualization = ({ distributionText, marketText }) => {
  // Extract financial metrics
  const budgetRange = extractBudgetRange(distributionText);
  const boxOfficeProjection = extractBoxOfficeProjection(distributionText);
  const theaterCount = extractTheaterCount(distributionText);
  const streamingViability = extractStreamingViability(marketText);

  // If no metrics found, don't render
  if (!budgetRange && !boxOfficeProjection && !theaterCount && !streamingViability) {
    return null;
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 3,
        backgroundColor: brandColors.white,
        border: `1px solid ${brandColors.primary}15`
      }}
    >
      <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600, mb: 3 }}>
        Financial Projections & Metrics
      </Typography>

      <Grid container spacing={3}>
        {/* Budget Recommendation */}
        {budgetRange && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<MoneyIcon />}
              label="Recommended Budget"
              value={budgetRange.display}
              color="#388e3c"
              progress={budgetRange.percentage}
            />
          </Grid>
        )}

        {/* Box Office Projection */}
        {boxOfficeProjection && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<RevenueIcon />}
              label="Box Office Potential"
              value={boxOfficeProjection.display}
              color={brandColors.secondary}
              progress={boxOfficeProjection.percentage}
            />
          </Grid>
        )}

        {/* Theater Count */}
        {theaterCount && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<TheaterIcon />}
              label="Theater Count"
              value={theaterCount.display}
              color={brandColors.primary}
              progress={theaterCount.percentage}
            />
          </Grid>
        )}

        {/* Streaming Viability */}
        {streamingViability && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<ViewsIcon />}
              label="Streaming Viability"
              value={streamingViability.level}
              color={streamingViability.color}
              progress={streamingViability.percentage}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value, color, progress }) => (
  <Box
    sx={{
      p: 2,
      backgroundColor: `${color}08`,
      borderRadius: 2,
      border: `2px solid ${color}30`,
      textAlign: 'center',
      transition: 'all 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3
      }
    }}
  >
    <Box sx={{ color: color, mb: 1 }}>{icon}</Box>
    <Typography variant="caption" sx={{ color: brandColors.primary, opacity: 0.7, display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: color, fontWeight: 700, mb: 1 }}>
      {value}
    </Typography>
    {progress !== undefined && (
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: `${color}20`,
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 3
          }
        }}
      />
    )}
  </Box>
);

// Parsing Functions
function extractBudgetRange(text) {
  // Look for patterns like "$15-30M", "$40M+", "$20-40M"
  const rangeMatch = text.match(/\$(\d+)-(\d+)M/);
  if (rangeMatch) {
    const low = parseInt(rangeMatch[1]);
    const high = parseInt(rangeMatch[2]);
    const avg = (low + high) / 2;
    const percentage = Math.min((avg / 100) * 100, 100); // Scale to 100
    return {
      display: `$${low}-${high}M`,
      low,
      high,
      percentage
    };
  }
  
  const singleMatch = text.match(/\$(\d+)M\+/);
  if (singleMatch) {
    return {
      display: `$${singleMatch[1]}M+`,
      low: parseInt(singleMatch[1]),
      percentage: 75
    };
  }
  
  return null;
}

function extractBoxOfficeProjection(text) {
  // Look for domestic box office projections
  const match = text.match(/\$(\d+)M?-?\$?(\d+)?M\s*domestic/i);
  if (match) {
    const low = parseInt(match[1]);
    const high = match[2] ? parseInt(match[2]) : low;
    const avg = (low + high) / 2;
    const percentage = Math.min((avg / 200) * 100, 100); // Scale: $200M = 100%
    return {
      display: high > low ? `$${low}-${high}M` : `$${low}M`,
      low,
      high,
      percentage
    };
  }
  return null;
}

function extractTheaterCount(text) {
  // Look for screen/theater counts
  const match = text.match(/([\d,]+)-?([\d,]+)?\s*(?:screens?|theaters?)/i);
  if (match) {
    const low = parseInt(match[1].replace(/,/g, ''));
    const high = match[2] ? parseInt(match[2].replace(/,/g, '')) : low;
    const avg = (low + high) / 2;
    const percentage = Math.min((avg / 4000) * 100, 100); // Scale: 4000 screens = 100%
    return {
      display: high > low ? `${formatNumber(low)}-${formatNumber(high)}` : formatNumber(low),
      low,
      high,
      percentage
    };
  }
  return null;
}

function extractStreamingViability(text) {
  const lowerText = text.toLowerCase();
  
  if (lowerText.includes('streaming viability: high') || lowerText.includes('streaming potential is high')) {
    return { level: 'High', color: '#388e3c', percentage: 85 };
  }
  if (lowerText.includes('streaming') && (lowerText.includes('optimal') || lowerText.includes('natural home'))) {
    return { level: 'High', color: '#388e3c', percentage: 85 };
  }
  if (lowerText.includes('moderate') && lowerText.includes('streaming')) {
    return { level: 'Moderate', color: '#f57c00', percentage: 60 };
  }
  if (lowerText.includes('low') && lowerText.includes('streaming')) {
    return { level: 'Low', color: '#d32f2f', percentage: 30 };
  }
  
  return null;
}

function formatNumber(num) {
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export default MetricsVisualization;

